import _ from 'lodash';

import { MAX_DATE, MIN_DATE } from '@/constants/dates';
import { isDateInRange, parseDateOrFallback } from '@/utils/dates';
import { logPerformanceMetric } from '@/utils/metrics';

/**
 * Determines if an enrollment is effective (at a given date) given its coverage data.
 *
 * This function evaluates enrollment effectiveness by checking if it has effective coverage
 * within active enrollment periods. It uses intersection logic to determine if the
 * effective date falls within both enrollment and coverage periods.
 *
 * @param {Object} formattedEnrollment - The enrollment object containing coverage data
 * @param {string|Date} formattedEnrollment.eligibilityStartDate - Enrollment effective start date
 * @param {string|Date} formattedEnrollment.eligibilityEndDate - Enrollment effective end date
 * @param {Object} [formattedEnrollment.coverage] - (Optional) Single coverage object within enrollment
 * @param {Object[]} [formattedEnrollment.coverages] - Array of coverage objects within enrollment
 * @param {string|Date} [enrollment.coverages[].startDate] - Coverage effective start date (alternative: coverageStartDate)
 * @param {string|Date} [enrollment.coverages[].endDate] - Coverage effective end date (alternative: coverageEndDate)
 * @param {string|Date} [enrollment.coverages[].coverageStartDate] - Alternative coverage effective start date field
 * @param {string|Date} [enrollment.coverages[].coverageEndDate] - Alternative coverage effective end date field
 * @param {Date} [effectiveDate=new Date()] - The date to check effective coverage against (defaults to current date)
 * @returns {boolean} `true` if the enrollment has effective coverage at the effective date, `false` otherwise
 *
 * @example
 * const enrollment = {
 *   eligibilityStartDate: '2024-01-01',
 *   eligibilityEndDate: '2024-12-31',
 *   coverages: [{
 *     coverageStartDate: '2024-01-01',
 *     coverageEndDate: '2024-12-31'
 *   }]
 * };
 *
 * const isEffective = isEnrollmentEffective(enrollment); // Check against current date
 * const wasEffective = isEnrollmentEffective(enrollment, new Date('2024-06-15')); // Check against specific date
 */
const isEnrollmentEffective = (enrollment, effectiveDate = new Date()) =>
  logPerformanceMetric(
    'isEnrollmentEffective',
    () => {
      const formattedEnrollment = _.cloneDeep(enrollment);

      try {
        if (!formattedEnrollment || typeof formattedEnrollment !== 'object') {
          // Invalid enrollment object
          return false;
        }

        // Create a local variable to avoid parameter reassignment
        let validatedEffectiveDate = effectiveDate;
        if (
          !validatedEffectiveDate ||
          !(validatedEffectiveDate instanceof Date) ||
          Number.isNaN(validatedEffectiveDate.getTime())
        ) {
          // Invalid effective date provided; use current date as fallback
          validatedEffectiveDate = new Date();
        }

        // Parse enrollment dates with fallbacks and context
        const enrollmentEffectiveStartDate = parseDateOrFallback(
          formattedEnrollment?.eligibilityStartDate,
          MIN_DATE,
        );
        const enrollmentEffectiveEndDate = parseDateOrFallback(
          formattedEnrollment?.eligibilityEndDate,
          MAX_DATE,
        );

        if (!formattedEnrollment?.coverages?.length) {
          if (!_.isEmpty(formattedEnrollment.coverage)) {
            formattedEnrollment.coverages = [formattedEnrollment.coverage];
          } else {
            return false;
          }
        }

        // Validate coverages array structure
        if (!Array.isArray(formattedEnrollment.coverages)) {
          // Coverages is not an array
          return false;
        }

        // Check if any coverage within this enrollment is effective for the given date
        return formattedEnrollment.coverages.some((coverage) => {
          try {
            if (!coverage || typeof coverage !== 'object') {
              // Invalid coverage object
              return false;
            }

            // Parse coverage dates with fallbacks, supporting both naming conventions
            const coverageEffectiveStartDate = parseDateOrFallback(
              coverage?.startDate ?? coverage?.coverageStartDate,
              MIN_DATE,
            );
            const coverageEffectiveEndDate = parseDateOrFallback(
              coverage?.endDate ?? coverage?.coverageEndDate,
              MAX_DATE,
            );

            // Calculate the intersection of coverage and enrollment periods
            const intersectionStartDate =
              coverageEffectiveStartDate > enrollmentEffectiveStartDate
                ? coverageEffectiveStartDate
                : enrollmentEffectiveStartDate;
            const intersectionEndDate =
              coverageEffectiveEndDate < enrollmentEffectiveEndDate
                ? coverageEffectiveEndDate
                : enrollmentEffectiveEndDate;

            // Skip if the intersection creates an invalid date range
            if (intersectionStartDate > intersectionEndDate) {
              return false;
            }

            // Check if the effective date falls within the intersection period (inclusive)
            return isDateInRange({
              date: validatedEffectiveDate,
              startDate: intersectionStartDate,
              endDate: intersectionEndDate,
            });
          } catch (error) {
            // Exception while processing coverage
            return false;
          }
        });
      } catch (error) {
        // Exception while processing enrollment
        return false;
      }
    },
    {
      enrollment,
      effectiveDate: effectiveDate?.toISOString(),
    },
  );

export default isEnrollmentEffective;
